/**
 * Servicio de Autenticación
 */

// En desarrollo local, usar URL completa del backend. En producción (Vercel), usar ruta relativa
const getAuthApiUrl = () => {
  // Si hay una URL configurada explícitamente, usarla (útil para desarrollo local)
  if (process.env.REACT_APP_AUTH_URL) {
    return process.env.REACT_APP_AUTH_URL;
  }
  // Por defecto, usar ruta relativa (funciona si front y back están en el mismo dominio en Vercel)
  return '/auth';
};

const AUTH_API_URL = getAuthApiUrl();

export interface AuthResponse {
  success: boolean;
  message?: string;
  error?: string;
  user?: {
    id: number;
    email: string;
    preferredTheme?: string | null;
    role?: string;
    /** Solo presente cuando role === 'editor' */
    permissions?: string[];
  };
  token?: string;
  verificationToken?: string;
  resetToken?: string;
  theme?: string;
}

export interface RegisterData {
  email: string;
  password: string;
  confirmPassword: string;
}

export interface LoginData {
  email: string;
  password: string;
}

/**
 * Registrar nuevo usuario
 */
export const register = async (data: RegisterData): Promise<AuthResponse> => {
  try {
    const url = `${AUTH_API_URL}/register`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    });

    // Leer respuesta como texto primero para manejar errores
    const textResponse = await response.text();

    let result: AuthResponse;

    try {
      result = JSON.parse(textResponse);
    } catch (parseError) {
      return {
        success: false,
        error: response.status >= 500 
          ? 'Error del servidor. Por favor, intenta nuevamente más tarde.'
          : 'El servidor devolvió una respuesta inválida. Por favor, contacta al administrador.',
      };
    }

    if (!result.success) {

      // Mostrar información de debug si está disponible
      const debugInfo = (result as any).debug;
      if (debugInfo) {
        // Debug info disponible pero no se muestra en consola
      }
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error 
        ? error.message 
        : 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.',
    };
  }
};

/**
 * Login de usuario
 */
export const login = async (data: LoginData): Promise<AuthResponse> => {
  try {

    const response = await fetch(`${AUTH_API_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include', // Para mantener la sesión
    });

    // Verificar cookies después de la respuesta

    // Verificar que la respuesta sea JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Leer respuesta para ver qué devolvió
      const textResponse = await response.text();
      // Si es un error 404 o similar, probablemente el backend no está corriendo
      if (response.status === 404 || textResponse.includes('<!DOCTYPE')) {
        return {
          success: false,
          error: 'El servidor no está disponible. Verifica que el backend esté corriendo y que la URL esté configurada correctamente.',
        };
      }

      return {
        success: false,
        error: 'El servidor devolvió una respuesta inválida. Por favor, contacta al administrador.',
      };
    }

    const result: AuthResponse = await response.json();

    if (result.success && result.user) {
      // Guardar información del usuario en localStorage
      try {
      localStorage.setItem('user', JSON.stringify(result.user));
      localStorage.setItem('authToken', result.token || '');
      } catch (storageError) {
        // Error al guardar en localStorage - silenciado
        // Continuar aunque falle localStorage (puede ser modo privado)
      }
    } else {
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al iniciar sesión',
    };
  }
};

/**
 * Cerrar sesión
 */
export const logout = async (): Promise<AuthResponse> => {
  try {
    const url = `${AUTH_API_URL}/logout`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    // Esperar la respuesta antes de limpiar el estado
    const textResponse = await response.text();

    let result: AuthResponse;
    try {
      result = JSON.parse(textResponse);
    } catch (parseError) {
      result = {
        success: false,
        error: 'Error al procesar respuesta del servidor'
      };
    }

    // Limpiar localStorage DESPUÉS de recibir la respuesta
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('theme'); // Remover tema para que los modales de autenticación no hereden estilos oscuros

    // Emitir evento de logout para sincronizar con otras pestañas
    localStorage.setItem('logoutEvent', Date.now().toString());
    localStorage.removeItem('logoutEvent'); // Remover inmediatamente para que el evento se dispare

    // CRÍTICO: Remover tema oscuro del documento para que los modales de autenticación no hereden estilos oscuros
    document.documentElement.classList.remove('dark-theme');
    document.documentElement.classList.add('light-theme');

    // Limpiar todas las cookies relacionadas con la sesión
    const cookieNames = ['connect.sid', 'session', 'sessionid'];
    cookieNames.forEach((cookieName) => {
      // Limpiar con diferentes paths y domains
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.smartcityexposantiagodelestero.com;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=perfil.smartcityexposantiagodelestero.com;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=smartcityexposantiagodelestero.com;`;
    });

    // También limpiar cualquier cookie que contenga 'connect' o 'session'
    const allCookies = document.cookie.split(";");
    allCookies.forEach((c) => {
      const cookieName = c.trim().split("=")[0];
      if (cookieName.toLowerCase().includes('connect') || cookieName.toLowerCase().includes('session')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.smartcityexposantiagodelestero.com;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=perfil.smartcityexposantiagodelestero.com;`;
      }
    });

    // Limpiar parámetros de la URL para evitar que AuthWrapper los procese
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return result;
  } catch (error) {

    // Limpiar localStorage incluso si hay error
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('theme'); // Remover tema incluso si hay error

    // Emitir evento de logout para sincronizar con otras pestañas
    localStorage.setItem('logoutEvent', Date.now().toString());
    localStorage.removeItem('logoutEvent'); // Remover inmediatamente para que el evento se dispare

    // CRÍTICO: Remover tema oscuro del documento incluso si hay error
    document.documentElement.classList.remove('dark-theme');
    document.documentElement.classList.add('light-theme');

    // Limpiar cookies incluso si hay error
    document.cookie.split(";").forEach((c) => {
      const cookieName = c.trim().split("=")[0];
      if (cookieName.includes('connect.sid') || cookieName.includes('session')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.smartcityexposantiagodelestero.com;`;
      }
    });

    // Limpiar parámetros de la URL incluso si hay error
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al cerrar sesión',
    };
  }
};

/**
 * Verificar sesión actual
 */
export const verifySession = async (): Promise<AuthResponse> => {
  try {

    const response = await fetch(`${AUTH_API_URL}/verify-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    // Si la respuesta no es OK, verificar si es 401
    if (!response.ok && response.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      const result: AuthResponse = {
        success: false,
        error: 'Sesión expirada. Por favor, inicia sesión nuevamente.',
      };
      // Agregar status para que pueda ser detectado
      (result as any).status = 401;
      return result;
    }

    const result: AuthResponse = await response.json();

    if (result.success && result.user) {
      localStorage.setItem('user', JSON.stringify(result.user));
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      // Si no es exitoso y el status es 401, agregarlo al resultado
      if (response.status === 401) {
        (result as any).status = 401;
      }
    }

    return result;
  } catch (error: any) {
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    const result: AuthResponse = {
      success: false,
      error: error instanceof Error ? error.message : 'Error al verificar sesión',
    };
    // Si el error tiene status 401, agregarlo
    if (error?.status === 401 || error?.statusCode === 401) {
      (result as any).status = 401;
    }
    return result;
  }
};

/**
 * Solicitar recuperación de contraseña
 */
export const requestPasswordReset = async (email: string): Promise<AuthResponse> => {
  try {
    const url = `${AUTH_API_URL}/request-reset`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
      credentials: 'include',
    });

    const textResponse = await response.text();

    let result: AuthResponse;

    try {
      result = JSON.parse(textResponse);
    } catch (parseError) {
      return {
        success: false,
        error: 'Error del servidor. Por favor, intenta nuevamente.',
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error 
        ? error.message 
        : 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.',
    };
  }
};

/**
 * Resetear contraseña
 */
export const resetPassword = async (token: string, password: string): Promise<AuthResponse> => {
  try {

    const response = await fetch(`${AUTH_API_URL}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, password }),
      credentials: 'include',
    });

    const textResponse = await response.text();
    let result: AuthResponse;

    try {
      result = JSON.parse(textResponse);
    } catch (parseError) {
      return {
        success: false,
        error: 'Error del servidor. Por favor, intenta nuevamente.',
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error 
        ? error.message 
        : 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.',
    };
  }
};

/**
 * Verificar email con código de 6 dígitos
 */
export const verifyEmailCode = async (email: string, code: string): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${AUTH_API_URL}/verify-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, code }),
    });

    const result: AuthResponse = await response.json();

    if (result.success && result.user) {
      localStorage.setItem('user', JSON.stringify(result.user));
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al verificar código',
    };
  }
};

/**
 * Reenviar código de verificación
 */
export const resendVerificationCode = async (email: string): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${AUTH_API_URL}/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    const result: AuthResponse = await response.json();
    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al reenviar código',
    };
  }
};

/**
 * Verificar email (endpoint antiguo con token - mantener para compatibilidad)
 */
export const verifyEmail = async (token: string): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${AUTH_API_URL}/verify?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const result: AuthResponse = await response.json();
    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al verificar email',
    };
  }
};

/**
 * Actualizar preferencia de tema
 */
export const updateThemePreference = async (theme: 'light' | 'dark'): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${AUTH_API_URL}/update-theme`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ theme }),
    });

    const result: AuthResponse = await response.json();
    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al actualizar la preferencia de tema',
    };
  }
};

/**
 * Obtener usuario actual desde localStorage
 */
export const getCurrentUser = () => {
  try {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
    }
  } catch (storageError) {
    // Error al leer de localStorage - silenciado
  }
  return null;
};

/**
 * Verificar si el usuario está autenticado
 */
export const isAuthenticated = (): boolean => {
  try {
  return !!localStorage.getItem('user') && !!localStorage.getItem('authToken');
  } catch (storageError) {
    // Error al verificar autenticación en localStorage - silenciado
    return false;
  }
};

/**
 * Iniciar sesión con Google OAuth
 */
export const loginWithGoogle = (): void => {
  const authUrl = process.env.REACT_APP_AUTH_URL || '/auth';
  window.location.href = `${authUrl}/google`;
};

/**
 * Eliminar cuenta de usuario
 */
export const deleteAccount = async (): Promise<AuthResponse> => {
  try {

    const url = `${AUTH_API_URL}/delete-account`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const textResponse = await response.text();

    let result: AuthResponse;
    try {
      result = JSON.parse(textResponse);
    } catch (parseError) {
      result = {
        success: false,
        error: 'Error al procesar respuesta del servidor'
      };
    }

    if (result.success) {
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');

      // Limpiar cookies
      const cookieNames = ['connect.sid', 'session', 'sessionid'];
      cookieNames.forEach((cookieName) => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.smartcityexposantiagodelestero.com;`;
      });

      // Limpiar parámetros de la URL
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al eliminar cuenta',
    };
  }
};
